<template>
  <div :key="refresh" style="z-index: 100">
    <v-skeleton-loader v-if="policyLoading" type="article, list-item-three-line@9"></v-skeleton-loader>
    <div v-else>
      <v-divider class="mx-auto my-1 secondary" style="border-width: 1px"></v-divider>

      <!--    GENERAL POLICY FIELDS -->
      <v-expansion-panels class="rounded-0" focusable popout v-model="panel.general">
        <v-expansion-panel>
          <v-expansion-panel-header class="pr-1 pr-sm-5 title">
            <v-row class="pl-5" style="z-index: 1150">Edit Policy
              <information-dialog-icon confirmClass="mr-3 mb-3 title" confirmColor="secondary darken-1"
                                       icon-class="pb-1 ml-2" style="margin-top: -2px"
                                       tclass="headline grey lighten-2 mb-8" title="Editing Fields">
                <p>Date format must be <span style="color:green">"{{ rulesDateFormat }}"</span>
                  for "SAVE POLICY" button to work.<br>
                  Dates that are not real, such as <span style="color:red">"02-30-2020"</span> will not be allowed.</p>
                <p><b>Expiry</b> & <b>Retro</b> allow a valid date or a string from their dropdown.
                  To see dropdown, click the field, first clearing the field if necessary. To enter a date
                  when the field has a string in it, select 'Enter Date' from dropdown.</p>
                <p>If a field already has an invalid entry, that will be allowed to remain, but if the field
                  is edited, the correct format will be required.</p>
                Amount fields may begin "-" if needed. Decimal must have 2 digits:<br>
                <span style="color:green">YES: 0, 30.03, -2.44, 123, -44, 3.50, 12345.55</span><br>
                <span style="color:red">NO: .00, -0, 1.234, 3.4, 05, -004.31, 1,234.00</span><br><br>
                Percent fields may range between -1 & 1, and have up to 5 decimals:<br>
                <span style="color:green">YES: 0, 1, -1, .123, -.44, .999, 0.99999, .0725, -1.00</span><br>
                <span style="color:red">NO: .123456, -0, 1.2, 3.45, 01, -001.31, 1.001, -1.001</span><br><br>
                Clicking <v-icon>mdi-eye-outline</v-icon>/<v-icon>mdi-eye-off-outline</v-icon>
                will show/hide the detailed rows.<br>
                <span class="mb-n3">Press return in a policy/option field to save the whole policy/option.</span>
              </information-dialog-icon>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0 mx-n6">
            <v-btn class="pt-1 mt-1 elevation-9 rounded-0 secondary" @click="save(policy, -1)" block
                   :dark="valid && !$_eq(policy,policyClean)" :disabled="!valid || $_eq(policy,policyClean)"
                   small tile>Save Policy</v-btn>
            <v-card class="px-2 pt-2 mx-auto mt-1 mb-n4 scroll rounded-0" id="policyForm" style="max-width: 1388px">
              <v-form ref="policyForm" v-model="valid">
                <div v-for="(row, index) in rows" class="d-flex flex-nowrap px-1" :key="row">
                  <v-col
                    :class="row >= 5 ? 'my-n2' : ''"
                    class="px-2 ma-0"
                    :key="refreshFees + field.name"
                    v-for="field in policyFields[index]"
                  >
                    <span v-if="field.name === 'Button'">
                      <button-tip btnclass="ml-n1 mt-2 mr-n3" @click="showFees = true, rows = 7, refreshFees = !refreshFees"
                                  icon iname="mdi-eye-outline" tip="Show Details" v-if="showFees !== true" xsmall/>
                      <button-tip btnclass="ml-n1 mt-2 mr-n3" @click="showFees = false, rows = 4, refreshFees = !refreshFees"
                                  icon iname="mdi-eye-off-outline" tip="Hide Details" v-if="showFees === true" xsmall/>
                    </span>
                    <smart-input
                      allow-str
                      :chips="field.multiple"
                      :class="row > 4 ? 'font-weight-light' : ''"
                      class="changedfield small"
                      :clean-value="policyClean[field.val]"
                      clearable
                      :deletable-chips="field.multiple"
                      dense
                      @enter="valid === true && !$_eq(policy,policyClean) ? save(policy, -1) : ''"
                      :did-scroll="didScrollX"
                      :dirty-check="!policyLoading && !readonlyFields.includes(field.val)"
                      :items="field.select ? selects.includes(field.val) ? policyItems[field.val] : $_lists[field.list] : []"
                      :input-props="field.val === 'depid' ? { appendIcon: '' } : {}"
                      :label="field.name"
                      :menu-props="{ auto: true, contentClass: 'compact long', minWidth: field.width}"
                      :multiple="field.multiple"
                      :no-data-text="policyLoading ? 'loading...' : 'no data available'"
                      :rules="policyRules(field)"
                      :readonly="readonlyFields.includes(field.val)"
                      :small-chips="field.multiple"
                      :style="{ width: field.width, fontSize: '14px' }"
                      :tabindex="'1'"
                      :type="getType(field)"
                      v-else
                      v-model="policy[field.val]"
                    ></smart-input>
                  </v-col>
                </div>
              </v-form>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!--    OPTIONS  -->
      <v-expansion-panels class="mt-2 rounded-0" focusable :key="refreshOptions" popout v-model="panel.options">
        <v-expansion-panel class="pa-0 ma-0">
          <v-expansion-panel-header class="pl-5 pr-1 pr-sm-5 py-0">
            <v-row class="d-flex flex-row">
              <div class="pl-3 mt-8 mb-n2">
                <span class="mt-2 title">Options <span class="body-1">({{policiesAdded.length}})</span></span>
                <information-dialog-icon confirmClass="mr-3 mb-3 title" confirmColor="secondary darken-1"
                  icon-class="ml-1 mt-n1" title="Options Policies" tclass="headline grey lighten-2 mb-8">
                  <i v-if="program">Program: {{ program.prid }} - {{ program.carrier }}  {{ program.type }}<br><br></i>
                  Click "Options" to see list of policies that are in the same program and group with the current policy
                  being edited. Click "+" to copy the current policy being edited and add it to the group with new
                  limits, deductible, and premium amounts.
                </information-dialog-icon>
              </div>
              <v-spacer/>
              <v-form v-model="policyToAddValid">
                <div class="d-flex flex-nowrap px-0 mx-auto mt-3 mb-1">
                  <!--     add new linked policy with: limit deduc premium-->
                  <v-col
                    class="px-2 mx-1 ma-0 mt-1"
                    @click.stop=""
                    :key="field.name"
                    v-for="field in addPolicyFields">
                    <smart-input
                      class="changedfield small"
                      clearable
                      dense
                      :items="field.select ? $_lists[field.list] : []"
                      :label="field.name"
                      :no-data-text="policyLoading ? 'loading...' : 'no data available'"
                      :rules="field.val === 'premium' ? [rules.amt] : []"
                      :style="{ width: field.width, fontSize: '14px' }"
                      :type="field.select ? 'VAutocomplete' : 'VTextField'"
                      v-model="policyToAdd[field.val]"
                    ></smart-input>
                  </v-col>
                  <!-- 11 Feb 2026 JCH: use shared button-tip wrapper for consistent tooltip + click behavior -->
                  <button-tip btnclass="mt-3 mr-2 ml-2 mr-sm-4" @click="addPolicy" color="secondary" elevation="5"
                              :disabled="!policyToAdd.limits || !policyToAdd.deduc || !policyToAdd.premium
                              || !policyToAddValid" fab icolor="white" iname="mdi-plus" :loading="policyToAddLoading"
                              stop tip="Add Policy Copy to Group" xsmall/>
                </div>
              </v-form>
              <v-spacer/><v-spacer/>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0 ml-n3 mr-n6" :key="refreshOptionFees">
            <v-card color="grey lighten-4" class="px-0 pt-2 mx-n2 mt-2 mb-n4 rounded-0">
              <v-form v-model="policyAddedValid">
                <div class="scroll pt-2" style="max-height: 446px">
                  <div v-for="(row, index) in policiesAdded.length" class="pl-4 mb-2"
                       :key="row + 'option' + showOptionFees[index]" style="margin-left: -3px">
                    <v-row class="d-flex flex-nowrap px-1">
                      <button-tip btnclass="ml-0 mr-n2 mt-3" @click="save(policiesAdded[index], index)"
                                  :disabled="$_eq(policiesAdded[index],policiesAddedClean[index]) || !policyAddedValid"
                                  icon iname="mdi-content-save-edit-outline" tip="Save Option"/>
                      <button-tip btnclass="ml-0 mr-n2 mt-3" @click="showOptionFees[index] = false, refreshOptionFees = !refreshOptionFees"
                                  icon iname="mdi-eye-off-outline" tip="Hide Option Details" v-if="showOptionFees[index] === true"/>
                      <button-tip btnclass="ml-0 mr-n2 mt-3" @click="showOptionFees[index] = true, refreshOptionFees = !refreshOptionFees"
                                  icon iname="mdi-eye-outline" tip="Show Option Details" v-if="showOptionFees[index] !== true"/>
                      <button-tip btnclass="ml-0 mr-n3 mt-3" @click="deletePolicyOption(policiesAdded[index])" icolor="error"
                                  icon iname="mdi-trash-can-outline" tip="Delete Option" v-if="groups.includes('admin')"/>
                      <v-col
                        class="ma-0"
                        :key="field.name + 'row3'"
                        style="padding-left: 4.9px; padding-right: 4.9px"
                        v-for="field in policyFields[3]">
                        <smart-input
                          allow-str
                          class="changedfield small"
                          :clean-value="policiesAddedClean[index][field.val]"
                          clearable
                          dense
                          :dirty-check="!policiesAddedLoading && !readonlyFields.includes(field.val)"
                          @enter="!$_eq(policiesAdded[index],policiesAddedClean[index])
                            ? save(policiesAdded[index], index) : ''"
                          :items="field.select ? $_lists[field.list] : []"
                          :label="field.name"
                          :input-props="field.val === 'depid' ? { appendIcon: '' } : {}"
                          :no-data-text="policyLoading ? 'loading...' : 'no data available'"
                          :readonly="readonlyFields.includes(field.val)"
                          :rules="field.select || readonlyFields.includes(field.val) ||
                            $_eq(policiesAddedClean[index][field.val],policiesAdded[index][field.val]) ? [] : [rules.amt]"
                          :style="field.name === 'Limit' ? { width: '160px', fontSize: '14px' }
                            : { width: field.width, fontSize: '14px' }"
                          :tabindex="'1'"
                          :type="field.select ? 'VAutocomplete' : 'VTextField'"
                          v-if="field.name !== 'Button'"
                          v-model="policiesAdded[index][field.val]"
                        ></smart-input>
                      </v-col>
                    </v-row>
                    <v-row v-for="i in [4,5,6]" class="d-flex flex-nowrap mt-n5 option-details-row" v-if="showOptionFees[index] === true">
                      <div :style="groups.includes('admin') ? 'min-width: 91px' : 'min-width: 70px'"></div>
                      <v-col
                        class="px-1 ma-0"
                        :key="field.name"
                        v-for="field in policyFields[i]">
                        <smart-input
                          allow-str
                          class="changedfield font-weight-light small"
                          :clean-value="policiesAddedClean[index][field.val]"
                          clearable
                          dense
                          @enter="!$_eq(policiesAdded[index],policiesAddedClean[index])
                            ? save(policiesAdded[index], index) : ''"
                          :dirty-check="!policiesAddedLoading && !readonlyFields.includes(field.val)"
                          :items="optionFieldItems(field, policiesAdded[index])"
                          :label="field.name"
                          :no-data-text="policyLoading ? 'loading...' : 'no data available'"
                          :readonly="readonlyFields.includes(field.val)"
                          :rules="field.select || readonlyFields.includes(field.val)
                            || $_eq(policiesAddedClean[index][field.val],policiesAdded[index][field.val]) ? []
                            : percentFields.includes(field.val) ? [rules.percent] : [rules.amt]"
                          :style="{ width: field.width, fontSize: '14px' }"
                          :tabindex="'1'"
                          :type="field.select ? 'VAutocomplete' : 'VTextField'"
                          v-model="policiesAdded[index][field.val]"
                        ></smart-input>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </v-form>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!--    TEXT FIELDS (HTML) -these 4 fields are updated to the whole group, unless grpnum = 0, then just update policy -->
      <v-expansion-panels class="my-2 rounded-0" focusable popout v-model="panel.text">
        <v-expansion-panel class="ma-0 top-offset" @click="scrollIntoView('text')" id="text">
          <v-expansion-panel-header class="pl-5 pr-1 pr-sm-5 title">Text Fields</v-expansion-panel-header>
          <v-expansion-panel-content class="px-0 mx-n6 mt-n2 mb-n5">
            <v-btn block class="elevation-9 rounded-0 secondary" @click="save(policy, -1)" small
                   v-if="policy.grpnum === 0">Save Policy</v-btn>
            <v-btn block class="elevation-6 rounded-0 secondary" @click="saveToGroup(policy)" small
                   v-else>Save text fields to group</v-btn>
            <v-row class="px-0 mx-0 mt-1 row-wrap" dense>
              <v-col class="px-0 mx-0" cols="12" :key="field + resetEditor" v-for="field in htmlFields">
                <v-card class="pa-1 mx-0 mt-n1 rounded-0">
                  <v-row justify="start" class="mr-4 mb-n3">
                    <v-card-title class="mt-n2">{{ field.toUpperCase() }}</v-card-title>
                    <v-spacer></v-spacer>
                    <v-btn v-if="rendered[field]" text x-large color="secondary darken-1"
                           class="pb-0 font-weight-bold" @click="revertToPlainText(field)">
                      Revert To Plain Text
                    </v-btn>
                    <v-btn v-else-if="!rendered[field]" text x-large color="secondary darken-2"
                           class="pb-0 font-weight-bold" @click="convertToHtml(field)">
                      Convert To HTML
                    </v-btn>
                    <information-dialog-icon confirmClass="mr-3 mb-3 title" confirmColor="secondary darken-1"
                      icon-class="mb-n5 mt-2 ml-n5 mr-n4" tclass="headline grey lighten-2 mb-8" title="Rendering HTML Field">
                      Converting to HTML means that you will be provided an HTML editor to edit with as
                      opposed to a plain text box. This will add HTML tags to your field, and give you more styling options. You
                      won't be able to see the HTML tags unless you edit it in in.dominioninsurance.com. If you have already
                      converted the field to be HTML text, a "revert to plain text" button will appear, and the system will
                      attempt to strip and replace any added HTML tags with plain text characters. Note that if you have a table
                      or other additional non-text elements, there will be no way to remove these and maintain the contents
                      of your field. Those HTML tags will remain, even after reverting to plain text.
                    </information-dialog-icon>
                  </v-row>
                  <ckeditor :config="editorConfig" type="classic" v-if="rendered[field]"
                            v-model="policy[field]" :editor="editor"
                  ></ckeditor>
                  <v-textarea class="px-3 pb-0 mx-1 mt-n1" dense rows="8"
                              style="margin-bottom: -8px" v-else v-model="policy[field]">
                  </v-textarea>
                </v-card>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!--    LEDGER FIELDS -->
      <v-expansion-panels class="my-2 rounded-0" focusable :key="refreshLedger + 'ledger'" popout v-model="panel.ledger">
        <v-expansion-panel class="top-offset" @click="scrollIntoView('ledger')" id="ledger">
          <v-expansion-panel-header class="pl-5 pr-1 pr-sm-5 title">
            Edit Ledgers
            <span
              v-if="showLedgerAmtSummary"
              :class="`ml-6 body-1 font-weight-medium ${showLedgerAmtWarning ? 'error--text' : 'secondary--text text--darken-2'}`"
              style="margin-top: 1px;"
            >
              <template v-if="$vuetify.breakpoint.xs">
                <span class="d-block">Ledger sum: {{ ledgerAmtTotalDisplay }}</span>
                <span class="d-block">Must be zero.</span>
              </template>
              <template v-else>
                Ledger sum is {{ ledgerAmtTotalDisplay }} (must be zero).
              </template>
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content :class="!ledgerLoading && !ledger.length ? 'pb-0 rounded-0' : 'pb-0 mr-n10 rounded-0'">
            <v-row v-if="!ledgerLoading && !ledger.length" class="mx-0 px-4" style="margin-top: 40px; margin-bottom: 30px;">
              <v-card
                v-if="bookPolicyErrorVisible"
                class="mx-auto pa-4 rounded-0"
                style="width: min(760px, 100%); background: #fff5f5; border: 1px solid rgba(183, 28, 28, 0.2);"
              >
                <div class="body-2" style="color: #7f1d1d;">{{ bookPolicyErrorMsg }}</div>
                <v-btn class="mt-2 px-2 rounded-0" color="secondary" text small @click="bookPreCheck">Try Again</v-btn>
              </v-card>
              <v-btn
                v-else
                class="py-3 mt-1 mx-auto elevation-9 rounded-0 secondary"
                style="width: min(760px, 100%);"
                :disabled="bookPolicyLoading || policyLoading"
                :loading="bookPolicyLoading"
                @click="bookPreCheck"
                large
                tile
              >
                Book Policy
              </v-btn>
            </v-row>
            <template v-else>
            <v-row class="mx-n4" style="margin-top: 1px; margin-bottom: 1px;">
              <v-btn
                block
                  class="pt-1 mt-1 mb-1 elevation-9 rounded-0 secondary"
                :disabled="ledgerLoading || ledgerSaving || !ledger.length || !hasLedgerChanges || hasInvalidLedgerChanges"
                :loading="ledgerSaving"
                @click="saveLedgers"
                small
                tile
              >
                Save Ledgers
              </v-btn>
            </v-row>
            <v-card class="pt-5 mx-n5 mb-16 mb-sm-n4 scroll">
              <v-row :class="`px-4 py-3 d-flex flex-nowrap ${row === 1 ? 'mt-1' : ''}`" v-for="row in ledger.length" :key="row">
                <v-col
                  class="px-2 mx-0"
                  :key="`ledger-${row}-${field.val}`"
                  v-for="field in ledgerFields[0]">
                  <smart-input
                    allow-str
                    :class="`my-n5 changedfield small ${field.val === 'amt' ? 'ledger-amt-input' : ''}`"
                    @blur="field.val === 'amt' ? normalizeLedgerAmount(row-1) : null"
                    @focusin.native="field.val === 'amt' ? onLedgerAmtFocusIn() : null"
                    @focusout.native="field.val === 'amt' ? onLedgerAmtFocusOut() : null"
                    @input="field.val === 'amt' ? onLedgerAmtInput() : null"
                    :clean-value="ledgerClean[row-1][field.val]"
                    clearable
                    :dirty-check="!ledgerLoading"
                    dense
                    :items="field.select ? field.val === 'cid' ? ledgerItems['cid'] : $_lists[field.list] : []"
                    :item-text="field.val === 'cid' ? 'sort' : 'text'"
                    :item-value="field.val === 'cid' ? 'cid' : 'value'"
                    :label="field.name"
                    :rules="ledgerRules(field, row-1)"
                    :style="{ width: field.width, fontSize: '14px' }"
                    :type="field.select ? 'VAutocomplete' : 'VTextField'"
                    v-model="ledger[row-1][field.val]"
                  ></smart-input>
                </v-col>
              </v-row>
            </v-card>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!--    EDIT FILES -->
      <v-expansion-panels class="rounded-0" focusable popout v-model="panel.files">
        <v-expansion-panel @click="scrollIntoView('files')">
          <v-expansion-panel-header class="pl-5 pr-1 pr-sm-5 title">Files</v-expansion-panel-header>
          <v-expansion-panel-content class="pl-3 pb-3" :key="refresh">
            <span id="files"><EditFiles ref="editFiles" v-bind="{ fid, groups, type, typeId }"/></span>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Add space at bottom for mobile friendliness -->
      <v-row class="mb-16 mb-sm-1"></v-row>

      <snack :color="$_snack.color" :msg="$_snack.msg" v-model="$_snack.open" v-if="$_snack.cmpnt==='EditPolicy'"></snack>

      <ActionDialog :value="unsavedPolicyChanges" @input="$emit('input', $event)" max-width="700"
                    title="Book Policy" confirm-text="Proceed" confirm-color="red lighten-2"
                    @close="unsavedPolicyChanges = false" @confirm="save(policy,-1,true)" divider>
          There are unsaved changes to this policy.  Click "Proceed" to save changes to policy and book policy.
      </ActionDialog>
    </div>
  </div>
</template>

<script>
import ActionDialog from '../../ActionDialog';
import ClassicEditor from "ckeditor5-build-classic-plus";// npm install --save ckeditor5-build-classic-plus
import currency from 'currency.js';
import debounce from 'lodash/debounce';
import EditFiles from "./EditFiles";
import InformationDialogIcon from "../../InformationDialogIcon";
import { ContactAPIService } from "@/servicehandlers/ContactAPIService";
import { FirmsAPIService } from '@/servicehandlers/FirmsAPIService';
import { LedgersAPIService } from "@/servicehandlers/LedgersAPIService";
import { ListsAPIService } from "@/servicehandlers/ListsAPIService";
import { PeopleAPIService } from "@/servicehandlers/PeopleAPIService";
import { PoliciesAPIService } from "@/servicehandlers/PoliciesAPIService";
import { ProgramsAPIService } from "@/servicehandlers/ProgramsAPIService";
import moment from "moment";

const contactsAPIService = new ContactAPIService();
const firmsAPIService = new FirmsAPIService();
const ledgersAPIService = new LedgersAPIService();
const listsAPIService = new ListsAPIService();
const peopleAPIService = new PeopleAPIService();
const policiesAPIService = new PoliciesAPIService();
const programsAPIService = new ProgramsAPIService();

export default {
  name: 'EditPolicy',
  components: { ActionDialog, EditFiles, InformationDialogIcon },
  props: {
    id: [String, Number],
    default() {
      return {};
    }
  },
  data() {
    return {
      currencyFields: ['premium','taxes','fees','total','down','doifee','slafee','profee','subfee','depfee','mgafee','srcfee','spnfee'],
      dates: ['incept','expiry','retro','bstamp','submitted','quoted','bndreq','booked','bound','issued','canceled'],
      dateSelects: ['expiry','retro'],
      dateStrings: [ 'Enter Date', 'See Note', 'Unlimited'],// for dateSelects dropdowns
      dateStrsAllowed: ['See Note', 'Unlimited', '', null, 'FPA'],// if value not in this list, type -> VTextField
      didScrollX: false,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [ 'heading', '|', 'bold', 'italic', 'underline', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo']
      },
      groups: [],
      htmlFields: ['forms', 'features', 'docs', 'note'],
      ledger: [],
      ledgerClean: [],
      ledgerItems: [],
      ledgerLoading: true,
      ledgerSaving: false,
      bookPolicyLoading: false,
      ledgerAmtEditing: false,
      ledgerAmtPaused: false,
      ledgerAmtPauseTimer: null,
      bookPolicyErrorVisible: false,
      bookPolicyErrorMsg: 'Book Policy API is not ready yet. Please try again shortly.',
      oldScrollLeft: 0,
      panel: {
        general: 0,
        files: '',
        options: '',
        text: '',
      },
      policyFormScrollBound: false,
      percentFields: ['doitax','slatax','procom','subcom','depcom','mgacom','srccom','spncom','dnrate','ccrate'],
      policiesAdded: [],// policies that were added to the group (options)
      policiesAddedClean: [],
      policiesAddedLoading: true,
      policy: {},
      policyAddedValid: false,
      policyClean: {},
      policyItems: [],
      policyLoading: true,
      policyToAdd: {},
      policyToAddLoading: false,
      policyToAddValid: false,
      program: {},
      // readonlyFields: ['taxes', 'fees', 'total'],
      // 11 Feb 2026 JCH: These fields are derived for display/filler and are not user-editable.
      readonlyFields: ['depid', 'mgacom', 'taxes', 'fees', 'total', 'down'],
      refresh: false,
      refreshFees: false,// for showing extra row of fees in main policy
      refreshLedger: false,
      refreshOptionFees: false,// for showing extra row of fees in policy option
      refreshOptions: false,// for refreshing when options list changes
      rendered: [],
      resetEditor: false,
      rows: 4,
      optionUndidItems: {},
      optionMgaidByPid: {},
      undidItemsByCid: {},
      rules: {
        date: (x) => (!x || this.valDate(x)) || `Only ${this.rulesDateFormat}`,
        dateSlct: (x) => (!x || this.valDate(x)) || this.dateStrsAllowed.includes(x) || `Only ${this.rulesDateFormat}`,
        // amt - no leading 0s, 2 digs after dec  YES: 0, 30.03, -2.44, 123, -44  NO: .00,  -0, 1.234, 3.4, 05, -004.31
        amt: (x) => (!x || /^ *(?!\.00)(-?[1-9]\d*|-?0?\.\d{2}|-?[1-9]\d*\.\d{2}|0) *$/.test(x)) || 'Invalid amount',
        // ledgerAmt - allow integer or up to 2 decimal places; normalized to 2 decimals on blur
        ledgerAmt: (x) => (!x || /^ *-?(?:0|[1-9]\d*)(?:\.\d{0,2})? *$/.test(x)) || 'Invalid amount',
        // percent - a value between -1 and 1, with 1-5 digits allowed after decimal
        percent: (x) => (!x || /^ *(-?[01]?\.\d{0,5}|0|-?1) *$/.test(x)) && Math.abs(x) <= 1 || 'Invalid %',
      },
      rulesDateFormat: 'MM-DD-YYYY',
      selects: ['ppid', 'expiry', 'retro', 'undid'],// fields with dropdown lists not in $_lists
      showFees: false,
      showOptionFees: [],
      type: 'policy',// for EditFiles.vue
      unsavedPolicyChanges: false,
      valid: true,
      isApplyingDerivedPolicy: false,
      isApplyingDerivedOptions: false,
    };
  },

  computed: {
    addPolicyFields() {
      return [
        {name: 'Limit',      val: 'limits', width: 'calc(20px + 14vw)', select: true, list: 'limits', sort: '' },
        {name: 'Deductible', val: 'deduc',  width: 'calc(30px + 14vw)', select: true, list: 'deduc',  sort: '' },
        {name: 'Premium',    val: 'premium',width: 'calc(7px + 14vw)' },
      ]
    },

    fid() {
      return this.policy.fid;// EditFiles.vue uses fid for firmsAPIService.getFirmFiles & FirmFilesUpload
    },

    ledgerFields() {
      let short  = '136px';
      let date   = '110px';
      let long   = '200px';
      return [// array of 1 row
        [
          {name: 'Entity',   val: 'cid',  width: short, select: true },
          {name: 'Type',     val: 'type', width: short, select: true, list: 'ledgertype', sort: '' },
          {name: 'Account',  val: 'acct', width: short, select: true, list: 'ledgeracct', sort: '' },
          {name: 'Ref',      val: 'num',  width: short},
          {name: 'Amount',   val: 'amt',  width: short},
          {name: 'Due',      val: 'due',  width: date },
          {name: 'Paid',     val: 'paid', width: date },
          {name: 'Deposited',val: 'dep',  width: date },
          {name: 'Note',     val: 'note', width: long },
        ],
      ];
    },
    hasLedgerChanges() {
      if (!Array.isArray(this.ledger) || !Array.isArray(this.ledgerClean)) return false;
      if (this.ledger.length !== this.ledgerClean.length) return true;
      for (let i = 0; i < this.ledger.length; i++) {
        if (!this.ledgerRowsEqual(this.ledger[i], this.ledgerClean[i])) return true;
      }
      return false;
    },
    hasInvalidLedgerChanges() {
      if (!Array.isArray(this.ledger) || !Array.isArray(this.ledgerClean)) return false;
      const fields = this.ledgerFields && this.ledgerFields[0] ? this.ledgerFields[0] : [];
      for (let i = 0; i < this.ledger.length; i++) {
        const cleanRow = this.ledgerClean[i];
        const liveRow = this.ledger[i];
        if (!cleanRow || !liveRow) continue;
        for (let j = 0; j < fields.length; j++) {
          const fieldVal = fields[j].val;
          if (!this.ledgerFieldChanged(fieldVal, cleanRow, liveRow)) continue;
          if (!this.isLedgerFieldValid(fieldVal, liveRow[fieldVal])) return true;
        }
      }
      return false;
    },
    ledgerAmtTotalCents() {
      if (!Array.isArray(this.ledger) || !this.ledger.length) return 0;
      let total = currency(0);
      for (let i = 0; i < this.ledger.length; i++) {
        const row = this.ledger[i] || {};
        const amt = row.amt === null || row.amt === undefined || row.amt === '' ? 0 : row.amt;
        total = total.add(currency(amt || 0));
      }
      return total.intValue;
    },
    ledgerAmtTotalDisplay() {
      return currency(this.ledgerAmtTotalCents, { fromCents: true }).value.toFixed(2);
    },
    showLedgerAmtWarning() {
      return !this.ledgerLoading
        && Array.isArray(this.ledger)
        && this.ledger.length > 0
        && (!this.ledgerAmtEditing || this.ledgerAmtPaused)
        && this.ledgerAmtTotalCents !== 0;
    },
    showLedgerAmtSummary() {
      return !this.ledgerLoading
        && Array.isArray(this.ledger)
        && this.ledger.length > 0
        && (!this.ledgerAmtEditing || this.ledgerAmtPaused);
    },

    policyFields() {
      let shortest= '92px';
      let shorter = '124px';
      let short   = '151px';
      let first   = '172px';
      let double  = '318px';
      let feeWidth = '136px';
      return [// array of rows
        [
          {name: 'Producer', val: 'prodname',width: first, select: true, list: 'name', sort: '1'},
          {name: 'Zip',      val: 'zip',     width: short},
          {name: 'Insured',  val: 'insured', width: double },
          {name: 'Polnum',   val: 'polnum',  width: short},
          {name: 'Prior',    val: 'ppid',    width: double,select: true },
          {name: 'Sales',    val: 'sales',   width: short},
        ],
        [
          {name: 'Status',   val: 'status',  width: first, select: true, list: 'pstatus',sort: '1'},
          {name: 'Placement',val: 'is_new',  width: short, select: true, list: 'is_new', sort: '' },
          {name: 'Carrier',  val: 'carrier', width: short, select: true, list: 'carrier',sort: '0'},
          {name: 'Type',     val: 'type',    width: short, select: true, list: 'ptype',  sort: '0'},
          {name: 'Incept',   val: 'incept',  width: short},
          {name: 'Expiry',   val: 'expiry',  width: short, select: true },
          {name: 'Retro',    val: 'retro',   width: short, select: true },
          {name: 'Bstamp',   val: 'bstamp',  width: short},
        ],
        [
          {name: 'Submitted',val: 'submitted',       width: first},
          {name: 'Quoted',   val: 'quoted',          width: short},
          {name: 'Bindreq',  val: 'bndreq',          width: short},
          {name: 'Booked',   val: 'booked',          width: short},
          {name: 'Bound',    val: 'bound',           width: short},
          {name: 'Issued',   val: 'issued',          width: short},
          {name: 'Canceled', val: 'canceled',        width: short},
          {name: 'Reason',   val: 'cancellationcode',width: short, select: true, list: 'cancat',  sort: ''},
        ],
        [
          // {name: 'Group',      val: 'grpnum',  width: '140px'},
          {name: 'Button',         val: 'button', width: '0px' },
          {name: 'Limit',          val: 'limits', width: short,   select: true, list: 'limits', sort: '' },
          {name: 'Limit Type',     val: 'ltype',  width: short,   select: true, list: 'ltype',  sort: '0'},
          {name: 'Deductible',     val: 'deduc',  width: short,   select: true, list: 'deduc',  sort: '' },
          {name: 'Deductible Type',val: 'dtype',  width: short,   select: true, list: 'dtype',  sort: '0'},
          {name: 'Premium',        val: 'premium',width: shorter},
          {name: 'Taxes',          val: 'taxes',  width: shorter},
          {name: 'Fees',           val: 'fees',   width: shorter},
          {name: 'Total',          val: 'total',  width: shortest},
          {name: 'Down',           val: 'down',   width: shorter},
        ],
        [
          // {name: 'Program',   val: 'prid',   width: shortest },
          {name: 'DOI Tax',    val: 'doifee',  width: feeWidth},
          {name: 'SLA Tax',    val: 'slafee',  width: feeWidth},
          {name: 'Broker Fee', val: 'profee',  width: feeWidth},
          {name: 'Sub Fee',    val: 'subfee',  width: feeWidth},
          {name: 'Source Fee', val: 'srcfee',  width: feeWidth},
          {name: 'Sponsor Fee',val: 'spnfee',  width: feeWidth},
          {name: 'MGA Fee',    val: 'mgafee',  width: feeWidth},
          {name: 'Dep Amt',    val: 'depfee',  width: feeWidth},
          {name: 'Down Rate',  val: 'dnrate',  width: feeWidth},
        ],
        [
          {name: 'DOI %',       val: 'doitax',  width: feeWidth},
          {name: 'SLA %',       val: 'slatax',  width: feeWidth},
          {name: 'Broker %',    val: 'procom',  width: feeWidth},
          {name: 'Sub %',       val: 'subcom',  width: feeWidth},
          {name: 'Source %',    val: 'srccom',  width: feeWidth},
          {name: 'Sponsor %',   val: 'spncom',  width: feeWidth},
          {name: 'MGA %',       val: 'mgacom',  width: feeWidth},
          {name: 'Dep %',       val: 'depcom',  width: feeWidth},
          {name: 'Card Rate',   val: 'ccrate',  width: feeWidth},
        ],
        [
          // 20 Feb 2026 JCH: IDs row uses contact.sort labels (cid values), plus undid from people.plid by MGA contact.
          {name: 'DOI ID',       val: 'doiid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'SLA ID',       val: 'slaid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'Producer ID',  val: 'proid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'Sub ID',       val: 'subid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'Source ID',    val: 'srcid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'Sponsor ID',   val: 'spnid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'MGA ID',       val: 'mgaid',  width: feeWidth, select: true, list: 'cid'},
          {name: 'Und ID',       val: 'undid',  width: `calc((${feeWidth} * 2) + 16px)`, select: true},
        ],
          // {name: 'Group',      val: 'grpnum',  width: '98%',  select: true, multiple: true },
      ]
    },

    typeId() {
      if (!this.policy.pid) return null;
      return this.policy.pid; // EditFiles.vue attaches files to policy with this policy id
    }

  },

  watch: {
    policy: {
      handler() {
        // 11 Feb 2026 JCH: Recompute derived values as user types, but debounce to avoid UI thrash.
        if (!this.isApplyingDerivedPolicy && this.recomputePolicyDebounced) {
          this.recomputePolicyDebounced();
        }
        // if any policy values change, validate policyForm so user will see error message, needed because when expiry
        // & retro fields toggle between VTextField & VAutocomplete, validation resets & an invalid value could be saved
        this.$nextTick(() => {
          if (this.$refs.policyForm && typeof this.$refs.policyForm.validate === 'function') {
            this.$refs.policyForm.validate();
          }
        });
      },
      deep: true,
    },
    'policy.mgaid': {
      handler(newMgaid, oldMgaid) {
        if (newMgaid === oldMgaid) return;
        const clearUndid = oldMgaid !== undefined;
        // 20 Feb 2026 JCH: MGA contact drives depid and underwriter choices; changing MGA clears selected underwriter.
        this.syncPolicyCidFields(this.policy, clearUndid);
        this.loadUndidItemsForPolicy(this.policy, this.policyItems, false);
      }
    },
    policiesAdded: {
      handler() {
        // 11 Feb 2026 JCH: Keep option rows in sync with the same derived calc rules.
        if (!this.isApplyingDerivedOptions && this.recomputeOptionsDebounced) {
          this.recomputeOptionsDebounced();
        }
        this.syncOptionLinkFields();
      },
      deep: true,
    },
  },

  created: async function() {
    this.policyItems['expiry'] = this.dateStrings;// expiry dropdown
    this.policyItems['retro'] = [...this.dateStrings, 'FPA'];// retro dropdown adds 'FPA'
    await this.getPolicy();
    this.groups = this.$store.getters.groups;
    contactsAPIService.getContactList(this.$router).then(contacts => {
        contacts = contacts.filter(contact => contact.sort !== ''); // remove empty rows from dropdown
        this.ledgerItems['cid'] = contacts;// special case for dropdown
    });
    this.getLedger('initial');
    if (this.policy.prid) programsAPIService.getProgram(this.policy.prid, this.$router).then((program)  => {
      // console.debug('program', program);
      this.program = program || {};
      // TODO need to format program field after api/s available - list those with prid === this.policy.prid;
      // let programsFiltered = programs.filter(program => (program.prid === this.policy.prid));
    });
  },

  mounted() {
    this.handleDebouncedScroll = debounce(this.handleScroll, 20);// checks horiz scroll so smartInput menu can be closed
    this.recomputePolicyDebounced = debounce(() => {
      this.applyDerivedToPolicy(this.policy, true);
    }, 200);
    this.recomputeOptionsDebounced = debounce(() => {
      this.applyDerivedToOptions();
    }, 200);
    window.addEventListener('scroll', this.handleDebouncedScroll, true);// scroll e makes element available
  },

  beforeDestroy() {
    const policyFormEl = document.getElementById('policyForm');
    if (policyFormEl && this.handleDebouncedScrollX) {
      policyFormEl.removeEventListener('scroll', this.handleDebouncedScrollX, true);
    }
    window.removeEventListener('scroll', this.handleDebouncedScroll, true);
  },

  methods: {
    normalizeEntityId(value) {
      if (value === null || value === undefined || value === '') return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    },
    undidOptionText(person) {
      const sort = person && person.sort ? `${person.sort}`.trim() : '';
      const given = person && person.given ? `${person.given}`.trim() : '';
      const surname = person && person.surname ? `${person.surname}`.trim() : '';
      const byName = [given, surname].filter(Boolean).join(' ');
      if (sort && byName) return `${sort} - ${byName}`;
      return sort || byName || `${person && person.plid ? person.plid : ''}`;
    },
    optionFieldItems(field, optionPolicy) {
      if (!field || !field.select) return [];
      if (field.val === 'undid') {
        const key = optionPolicy && optionPolicy.pid !== undefined && optionPolicy.pid !== null
          ? `${optionPolicy.pid}`
          : '';
        return key ? (this.optionUndidItems[key] || []) : [];
      }
      return field.list && this.$_lists && this.$_lists[field.list] ? this.$_lists[field.list] : [];
    },
    async getPeopleOptionsByCid(cid) {
      const normalizedCid = this.normalizeEntityId(cid);
      if (!normalizedCid) return [];
      const cacheKey = `${normalizedCid}`;
      if (Array.isArray(this.undidItemsByCid[cacheKey])) return this.undidItemsByCid[cacheKey];
      const people = await peopleAPIService.getPeopleByCid(normalizedCid, this.$router).catch(() => []);
      const options = (Array.isArray(people) ? people : [])
        .filter((person) => this.normalizeEntityId(person.plid) !== null)
        .map((person) => ({
          ...person,
          text: this.undidOptionText(person),
          value: this.normalizeEntityId(person.plid),
        }));
      this.$set(this.undidItemsByCid, cacheKey, options);
      return options;
    },
    syncPolicyCidFields(targetPolicy, clearUndid) {
      if (!targetPolicy || typeof targetPolicy !== 'object') return;
      const mgaid = this.normalizeEntityId(targetPolicy.mgaid);
      targetPolicy.mgaid = mgaid;
      targetPolicy.depid = mgaid;
      if (clearUndid) targetPolicy.undid = null;
    },
    async loadUndidItemsForPolicy(targetPolicy, itemsContainer, isOption) {
      if (!targetPolicy || typeof targetPolicy !== 'object' || !itemsContainer) return;
      const mgaid = this.normalizeEntityId(targetPolicy.mgaid);
      const options = await this.getPeopleOptionsByCid(mgaid);
      const list = Array.isArray(options) ? options : [];
      if (isOption) {
        const key = targetPolicy.pid !== undefined && targetPolicy.pid !== null ? `${targetPolicy.pid}` : '';
        if (key) this.$set(itemsContainer, key, list);
      } else {
        this.$set(itemsContainer, 'undid', list);
      }
      const undid = this.normalizeEntityId(targetPolicy.undid);
      if (!list.some((item) => this.normalizeEntityId(item.value) === undid)) targetPolicy.undid = null;
    },
    syncOptionLinkFields() {
      const nextMgaidByPid = {};
      if (!Array.isArray(this.policiesAdded)) {
        this.optionMgaidByPid = {};
        this.optionUndidItems = {};
        return;
      }
      for (const option of this.policiesAdded) {
        if (!option || option.pid === undefined || option.pid === null) continue;
        const key = `${option.pid}`;
        const currentMgaid = this.normalizeEntityId(option.mgaid);
        const hasPrior = Object.prototype.hasOwnProperty.call(this.optionMgaidByPid, key);
        const priorMgaid = hasPrior
          ? this.optionMgaidByPid[key]
          : undefined;
        const changed = hasPrior && priorMgaid !== currentMgaid;
        nextMgaidByPid[key] = currentMgaid;
        // 20 Feb 2026 JCH: Keep depid hard-linked to mgaid and clear undid when MGA changes in options.
        this.syncPolicyCidFields(option, changed);
        if (changed || !Array.isArray(this.optionUndidItems[key])) {
          this.loadUndidItemsForPolicy(option, this.optionUndidItems, true);
        }
      }
      this.optionMgaidByPid = nextMgaidByPid;
    },
    resetLedgerAmtPauseTimer() {
      if (this.ledgerAmtPauseTimer) clearTimeout(this.ledgerAmtPauseTimer);
      this.ledgerAmtPauseTimer = null;
    },
    onLedgerAmtFocusIn() {
      this.ledgerAmtEditing = true;
      this.ledgerAmtPaused = false;
      this.resetLedgerAmtPauseTimer();
    },
    onLedgerAmtInput() {
      this.ledgerAmtPaused = false;
      this.resetLedgerAmtPauseTimer();
      this.ledgerAmtPauseTimer = setTimeout(() => {
        if (this.ledgerAmtEditing) this.ledgerAmtPaused = true;
      }, 800);
    },
    onLedgerAmtFocusOut() {
      this.resetLedgerAmtPauseTimer();
      setTimeout(() => {
        const active = document.activeElement;
        this.ledgerAmtEditing = !!(active && active.closest && active.closest('.ledger-amt-input'));
        if (!this.ledgerAmtEditing) this.ledgerAmtPaused = false;
      }, 0);
    },
    parseFiniteNumber(value) {
      if (value === null || value === undefined) return null;
      const trimmed = `${value}`.trim();
      if (trimmed === '') return null;
      const num = Number(trimmed);
      return Number.isFinite(num) ? num : null;
    },
    ledgerAmtEquivalent(cleanValue, liveValue) {
      if (this.$_eq(cleanValue, liveValue)) return true;
      const cleanNum = this.parseFiniteNumber(cleanValue);
      const liveNum = this.parseFiniteNumber(liveValue);
      if (cleanNum === null || liveNum === null) return false;
      return cleanNum === liveNum;
    },
    normalizeLedgerAmountValue(value) {
      const num = this.parseFiniteNumber(value);
      if (num === null) return value;
      return num.toFixed(2);
    },
    normalizeLedgerAmount(rowIndex) {
      if (!Array.isArray(this.ledger) || rowIndex < 0 || rowIndex >= this.ledger.length) return;
      const row = this.ledger[rowIndex];
      if (!row) return;
      const normalized = this.normalizeLedgerAmountValue(row.amt);
      if (!this.$_eq(normalized, row.amt)) this.$set(this.ledger[rowIndex], 'amt', normalized);
    },
    async refreshPolicyAfterBook() {
      let booked = false;
      for (const delayMs of [0, 250, 700]) {
        if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
        const freshPolicy = await policiesAPIService.getPolicy(this.id, this.$router).catch(() => null);
        if (!freshPolicy || typeof freshPolicy !== 'object') continue;
        if (freshPolicy.grpnum === null) freshPolicy.grpnum = 0;
        this.applyDerivedToPolicy(freshPolicy, true);
        this.policy = freshPolicy;
        this.policyClean = {...freshPolicy};
        if (freshPolicy.status === 'Book' || freshPolicy.status === 'purBook' || !!freshPolicy.booked) {
          booked = true;
          break;
        }
      }
      return booked;
    },
    sanitizeLedgerNoteForDisplay(note) {
      if (typeof note !== 'string') return note;
      return note.includes('�') ? note.replace(/�/g, "'") : note;
    },
    ledgerValuesEqual(fieldVal, cleanValue, liveValue) {
      if (fieldVal === 'amt') return this.ledgerAmtEquivalent(cleanValue, liveValue);
      return this.$_eq(cleanValue, liveValue);
    },
    ledgerRowsEqual(liveRow, cleanRow) {
      if (!liveRow || !cleanRow) return this.$_eq(liveRow, cleanRow);
      const keys = new Set([...Object.keys(liveRow), ...Object.keys(cleanRow)]);
      for (const key of keys) {
        if (!this.ledgerValuesEqual(key, cleanRow[key], liveRow[key])) return false;
      }
      return true;
    },
    ledgerFieldChanged(fieldVal, cleanRow, liveRow) {
      if (!cleanRow || !liveRow) return false;
      return !this.ledgerValuesEqual(fieldVal, cleanRow[fieldVal], liveRow[fieldVal]);
    },
    isLedgerFieldValid(fieldVal, value) {
      const dateFields = ['due', 'paid', 'dep'];
      if (fieldVal === 'amt') return this.rules.ledgerAmt(value) === true;
      if (dateFields.includes(fieldVal)) return this.rules.date(value) === true;
      return true;
    },
    applyDerivedToOptions() {
      if (!Array.isArray(this.policiesAdded) || this.policiesAdded.length < 1) return;
      this.isApplyingDerivedOptions = true;
      this.policiesAdded.forEach((policy) => this.applyDerivedToPolicy(policy, false));
      this.isApplyingDerivedOptions = false;
    },

    applyDerivedToPolicy(policy, markPolicyApplying) {
      if (!policy || typeof policy !== 'object') return;
      if (markPolicyApplying) this.isApplyingDerivedPolicy = true;

      const toNum = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
      };
      const toMoney = (value) => currency(value || 0).value;
      const roundPct = (value) => Number(toNum(value).toFixed(5));

      const premium = toMoney(policy.premium);
      const taxes = currency(toMoney(policy.doifee))
        .add(toMoney(policy.slafee))
        .add(currency(premium).multiply(toNum(policy.doitax)))
        .add(currency(premium).multiply(toNum(policy.slatax))).value;
      const fees = currency(toMoney(policy.mgafee))
        .add(toMoney(policy.profee))
        .add(toMoney(policy.subfee))
        .add(toMoney(policy.srcfee))
        .add(toMoney(policy.spnfee)).value;
      const total = currency(premium).add(taxes).add(fees).value;
      const downRate = toNum(policy.dnrate);
      const cardRate = policy.ccrate === null || policy.ccrate === undefined || policy.ccrate === '' ? 0 : toNum(policy.ccrate);
      const baseDown = currency(premium).multiply(downRate).add(toMoney(policy.profee));
      const down = baseDown.add(baseDown.multiply(cardRate)).value;

      // 11 Feb 2026 JCH: mgacom is the read-only filler so commission % fields sum to 1.00.
      const mgacom = roundPct(
        1 - (toNum(policy.procom) + toNum(policy.subcom) + toNum(policy.srccom) + toNum(policy.spncom) + toNum(policy.depcom))
      );

      policy.taxes = Number(taxes).toFixed(2);
      policy.fees = Number(fees).toFixed(2);
      policy.total = Number(total).toFixed(2);
      // Keep derived down in 2-decimal format so amt validation remains valid.
      policy.down = Number(down).toFixed(2);
      policy.mgacom = mgacom;

      if (markPolicyApplying) this.isApplyingDerivedPolicy = false;
    },

    addLedgerEntry() {
      ledgersAPIService.addLedger({pid: this.policy.pid}, this.$router).then(() => this.getLedger());
    },
    getChangedLedgers() {
      const changed = [];
      for (let i = 0; i < this.ledger.length; i++) {
        if (!this.ledgerRowsEqual(this.ledger[i], this.ledgerClean[i])) changed.push(this.ledger[i]);
      }
      return changed;
    },
    saveLedgers() {
      if (this.ledgerLoading || this.ledgerSaving || !this.hasLedgerChanges) return Promise.resolve();
      if (this.hasInvalidLedgerChanges) {
        this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', color: 'error', open: true, msg: 'Fix invalid ledger date/amount values before saving.'});
        return Promise.resolve();
      }
      this.ledgerSaving = true;
      const changed = this.getChangedLedgers();
      return ledgersAPIService.updateLedgersArray(changed, this.$router)
        .then(() => {
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg: 'Ledger changes have been saved.'});
          return this.getLedger();
        })
        .catch(() => {
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', color: 'error', open: true, msg: 'Failed to save ledger changes.'});
        })
        .finally(() => {
          this.ledgerSaving = false;
        });
    },

    addPolicy() {// copy this policy with user-entered limit deduc premium
      this.policyToAddLoading = true;
      let policyToAdd = JSON.parse(JSON.stringify(this.policy));
      policyToAdd = _.omit(policyToAdd, ['contract','fees','pid','taxes','total']);// omit fields w/ api errors
      // replace limits, deduc, and premium fields with user-entered values
      for (let fld of this.addPolicyFields) if (this.policyToAdd[fld.val]) policyToAdd[fld.val] = this.policyToAdd[fld.val];
      // 11 Feb 2026 JCH: Normalize percent strings (e.g. ".05") to numeric values before POSTing copied options.
      for (let fld of this.percentFields) {
        if (policyToAdd[fld] !== '' && policyToAdd[fld] !== null && policyToAdd[fld] !== undefined) {
          const n = Number(policyToAdd[fld]);
          if (Number.isFinite(n)) policyToAdd[fld] = n;
        }
      }
      // 11 Feb 2026 JCH: Re-apply percent split fields after create because addPolicy API may default some to 0.
      const percentUpdate = {};
      for (let fld of this.percentFields) {
        if (policyToAdd[fld] !== '' && policyToAdd[fld] !== null && policyToAdd[fld] !== undefined) {
          percentUpdate[fld] = policyToAdd[fld];
        }
      }
      this.panel.options = 0;
      policiesAPIService.addPolicy(policyToAdd, this.$router).then((added) => {
        const reapplyPercentSplit = () => Object.keys(percentUpdate).length
          ? policiesAPIService.updatePolicy(added.pid, percentUpdate, this.$router).catch(() => {})
          : Promise.resolve();

        this.policyToAddLoading = false;
        // if this.policy.grpnum = 0 then it isn't in a group so need to send both pids to updateGroupNumByPids API
        // this will assign both policies the same unique grpnum (update both policies with new grpnum)
        if (this.policy.grpnum === 0) policiesAPIService.updateGroupNumByPids(this.policy.pid, added.pid, this.$router).then((response) => {
          this.policy.grpnum = response.grpnum;
          this.policyClean.grpnum = response.grpnum;// update policyClean so state of 'SAVE POLICY' button won't change
          reapplyPercentSplit().then(() => this.getPoliciesFor('options'));// gets/sets rows for options (policies linked by group)
        });
        else {
          reapplyPercentSplit().then(() => this.getPoliciesFor('options'));// gets/sets rows for options (policies linked by group)
        }
          this.$store.dispatch('SET_SNACK', {cmpnt: 'EditPolicy', open: true, msg: 'Policy was added to the group.'});
      }).catch((e) => { this.policyToAddLoading = false });
    },

    book() {
      if (this.bookPolicyLoading || !this.policy || !this.policy.pid) return Promise.resolve();
      this.bookPolicyLoading = true;
      return policiesAPIService.bookPolicy(this.policy.pid, this.$router)
        .then(() => this.getLedger())
        .then(() => this.refreshPolicyAfterBook())
        .then((booked) => {
          if (booked || (Array.isArray(this.ledger) && this.ledger.length)) {
            this.bookPolicyErrorVisible = false;
            this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg: 'Policy was booked.'});
            return;
          }
          this.bookPolicyErrorMsg = 'Book request succeeded, but no ledger rows were returned yet. Please try again shortly.';
          this.bookPolicyErrorVisible = true;
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', color: 'warning', open: true, msg: 'Book request succeeded but no ledger rows were returned yet.'});
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null;
          this.bookPolicyErrorMsg = [404, 405, 501].includes(status)
            ? 'Booking is not available yet. Please try again soon.'
            : 'Failed to book policy. Please try again.';
          this.bookPolicyErrorVisible = true;
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', color: 'error', open: true, msg: 'Failed to book policy.'});
        })
        .finally(() => {
          this.bookPolicyLoading = false;
        });
    },

    bookPreCheck() {// check for unsaved policy changes before booking, alert user
      if (this.bookPolicyLoading || this.ledgerLoading) return;
      if (this.policy && (this.policy.status === 'Book' || this.policy.status === 'purBook' || !!this.policy.booked)) {
        this.bookPolicyErrorVisible = false;
        this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg: 'Policy is already booked.'});
        return;
      }
      if (!this.$_eq(this.policyClean, this.policy)) {
        this.unsavedPolicyChanges = true;
        return;
      }
      this.bookPolicyErrorVisible = false;
      this.book();
    },

    convertToHtml(field) {
      this.rendered[field] = true;
      this.policy[field] = this.policy[field].replace(/(?:\r\n|\r|\n)/g, '<br>');
      this.resetEditor = !this.resetEditor;
    },

    dateAdd(date, add) {// add number to year part of yyyy-mm-dd date
      let parts = date.split('-');
      parts[0] = parseInt(parts[0]) + add;
      return `${parts[0]}-${parts[1]}-${parts[2]}`;
    },

    dateReorder(date) {// mm-dd-yyyy -> yyyy-mm-dd - for comparing and sorting
      let parts = date.split('-');
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    },

    dateWithinNumYears(date, num) {// check if date is within num years +/- of this.policy.expiry (or incept +1)
      if (this.valDate(date)) {
        let expiry = this.valDate(this.policy.expiry)
            ? this.dateReorder(this.policy.expiry) : this.dateAdd(this.dateReorder(this.policy.incept),1);
        return this.dateAdd(expiry,-1 * num) <= date && date <= this.dateAdd(expiry, num);
      } else return true;
    },

    deletePolicyOption(policy) {
      policiesAPIService.deletePolicy(policy.pid, this.$router)
        .then((data) => {
          this.getPoliciesFor('options');// gets/sets rows for options (policies linked by group)
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg: 'Policy has been deleted.'});
        })
    },

    getLedger(time) {
      this.ledgerLoading = true;
      this.ledgerAmtEditing = false;
      this.ledgerAmtPaused = false;
      this.resetLedgerAmtPauseTimer();
      return ledgersAPIService.getLedgers(this.policy.pid, this.$router).then((ledger) => {
        const sortedLedger = (Array.isArray(ledger) ? ledger : [])
          .map((row, idx) => ({ row, idx }))
          .sort((a, b) => {
            const aLid = Number(a.row && a.row.lid);
            const bLid = Number(b.row && b.row.lid);
            const aHas = Number.isFinite(aLid);
            const bHas = Number.isFinite(bLid);
            if (aHas && bHas && aLid !== bLid) return aLid - bLid;
            if (aHas !== bHas) return aHas ? -1 : 1;
            return a.idx - b.idx;
          })
          .map((entry) => ({
            ...entry.row,
            amt: this.normalizeLedgerAmountValue(entry.row ? entry.row.amt : null),
            note: this.sanitizeLedgerNoteForDisplay(entry.row ? entry.row.note : null),
          }));
        this.ledger = sortedLedger;
        this.ledgerClean = JSON.parse(JSON.stringify(sortedLedger));
        this.ledgerLoading = false;
        if (time !== 'initial') {// if initial getLedger, don't scrollIntoView
          this.scrollIntoView('ledger');
          this.panel.ledger = 0;
          this.refreshLedger = !this.refreshLedger;
        }
      });
    },

    getPoliciesFor(fields) {// call api once to get policies for 'options' and/or for 'prior' dropdown
      firmsAPIService.getFirmPolicies(this.policy.fid, this.$router).then((policies) => {
        this.policyItems['ppid'] = [];
        if (!Array.isArray(this.policyItems['undid'])) this.policyItems['undid'] = [];
        this.policiesAdded = [];
        let validMainPolicyDate = this.valDate(this.policy.expiry) || this.valDate(this.policy.incept);
        for (let p of policies) {// check each policy to see if it goes in 'options' and/or 'prior' dropdown
          // check if one of policy.expiry & policy.incept is valid and one of p,expiry && p.incept is valid
          let validDates = validMainPolicyDate && (this.valDate(p.expiry) || this.valDate(p.incept));
          // if expiry is missing then set reordered expiry to reordered(incept + 1 year)
          if (validDates) p.reorderedExpiry = this.valDate(p.expiry) ? this.dateReorder(p.expiry) : this.dateAdd(this.dateReorder(p.incept),1);
          if (!validDates || this.dateWithinNumYears(p.reorderedExpiry, 1)) {
            if (fields.includes('prior')) {
              if (['Book','dncLost','Lost','Past','Tail'].includes(p.status)) {
                p.text = `${p.carrier || ''} (${p.type || ''}${p.type ? '-' : ''}${p.status || ''}) ${p.expiry || ''}`;
                p.value = p.pid;
                this.policyItems['ppid'].push(p);
              }
            }
            // if it isn't the main policy being edited, and grpnum nonzero and prid and grpnums match
            if (fields.includes('options') && p.pid !== this.policy.pid && p.grpnum !== 0
                && p.prid === this.policy.prid && p.grpnum === this.policy.grpnum) this.policiesAdded.push(p);
          }
        }
        if (fields.includes('prior')) {// sort 'prior' dropdown items by expiry
          this.policyItems['ppid'].sort(function(a, b) { return new Date(a.reorderedExpiry) - new Date(b.reorderedExpiry)});
        }
        if (fields.includes('options')) {// sort options by premium
          this.policiesAdded.sort(function(a, b) { return a.premium - b.premium });
          this.applyDerivedToOptions();
          this.syncOptionLinkFields();
          this.policiesAddedClean = JSON.parse(JSON.stringify(this.policiesAdded));
        }
        this.policiesAddedLoading = false;
        this.refreshOptions = !this.refreshOptions;
        if (this.policiesAdded.length < 1) this.panel.options = null;
      });
    },

    getPolicy: async function() {// get policy to edit, get firm for title, get policies for options & 'prior' dropdown
      try {
        this.policy = await policiesAPIService.getPolicy(this.id, this.$router);
        // console.debug('this.policy', this.policy)
        // grpnum = 0 is used to indicate policy is not in a group, grpnum should not be null, set to zero if null
        if (this.policy.grpnum === null) this.policy.grpnum = 0;
        this.syncPolicyCidFields(this.policy, false);
        this.applyDerivedToPolicy(this.policy, true);
        await this.loadUndidItemsForPolicy(this.policy, this.policyItems, false);
        this.policyClean = {...this.policy};
        this.getPoliciesFor(['options','prior']);
        this.policyLoading = false;
        this.$nextTick(() => this.$refs.editFiles && this.$refs.editFiles.getAllFiles(this.policy.fid, this.policy.pid));
        firmsAPIService.getSingleFirm(this.policy.fid, this.$router).then((firm) => {
          document.title=`${this.policy.fid}  ${firm.sort} - ${this.policy.incept} ${this.policy.carrier}`;
        })
      } catch (e) {
      }
    },

    getType(field) {// get the type of input field
      let type = '';// if it isn't a select or its a date field w/ val not in dateStrsAllowed, then type is 'VTextField'
      if (!field.select || this.dateSelects.includes(field.val)
        && !this.dateStrsAllowed.includes(this.policy[field.val])) type = 'VTextField';
      else type = 'VAutocomplete';
      return type;
    },

    handleScrollX(event) {// set didScrollX to true if scrolling was significant horizontally
      const policyFormEl = document.getElementById('policyForm');
      if (!policyFormEl) return;
      let scrollLeft = policyFormEl.scrollLeft;// get position
      let scrolledX = this.oldScrollLeft - scrollLeft;// get change in horizontal position
      if (Math.abs(scrolledX) > 50) {// > 50 px ?
        this.didScrollX = true;
        this.oldScrollLeft = scrollLeft;// reset oldScrollLeft to current scrollLeft to detect next movement
      }
      setTimeout(()=>{ this.didScrollX = false }, 10)
    },

    handleScroll(event) {// if user scrolls window, that will make element available, so then we can set event listener
      if (this.policyFormScrollBound) return;
      const policyFormEl = document.getElementById('policyForm');
      if (!policyFormEl) return;
      this.handleDebouncedScrollX = debounce(this.handleScrollX, 20);
      policyFormEl.addEventListener('scroll', this.handleDebouncedScrollX, true);
      this.policyFormScrollBound = true;
    },

    policyRules(field) {// get rules by field
      if (this.$_eq(this.policyClean[field.val],this.policy[field.val])) return [];// no rules if looks like clean value
      if (this.dates.includes(field.val)) return field.select ? [this.rules.dateSlct] : [this.rules.date];// date fields
      return this.currencyFields.includes(field.val) ?  [this.rules.amt] : this.percentFields.includes(field.val)
          ? [this.rules.percent] : [];// currency, percents & remaining fields
    },
    ledgerRules(field, rowIndex) {
      const dateFields = ['due', 'paid', 'dep'];
      const cleanRow = this.ledgerClean && this.ledgerClean[rowIndex] ? this.ledgerClean[rowIndex] : null;
      const liveRow = this.ledger && this.ledger[rowIndex] ? this.ledger[rowIndex] : null;
      if (!cleanRow || !liveRow) return [];
      if (!this.ledgerFieldChanged(field.val, cleanRow, liveRow)) return [];
      if (field.val === 'amt') return [this.rules.ledgerAmt];
      return dateFields.includes(field.val) ? [this.rules.date] : [];
    },

    revertToPlainText(field) {
      this.rendered[field] = false;
      this.policy[field] = this.policy[field].replace(/<br\s?\/?>|<\s?\/\s?p>/gm, '\n').replace(/<p\s?>/gm, '').replace(/&nbsp;/gm, ' ');
      this.resetEditor = !this.resetEditor;
    },

    saveToGroup(policy) {// update all policies w/matching fid, prid, & grpnum with forms, features, docs, status fields
      let updateData = {
        forms: policy.forms,
        features: policy.features,
        docs: policy.docs,
        note: policy.note,
      }
      policiesAPIService.updateMatchingGroups(policy.grpnum, policy.fid, policy.prid, updateData, this.$router)
        .then((response) => {
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg: 'Text fields have been saved to group.'});
        });
    },

    save(policy, index, book) {// -1 index indicates this.policy instead of option[0], option[1], etc.
      this.applyDerivedToPolicy(policy, index === -1);
      return policiesAPIService.updatePolicy(policy.pid, policy, this.$router)
        .then((data) => {
          if (book) this.book();
          if (index === -1) this.unsavedPolicyChanges = false;
          if (index === -1) this.policyClean = {...this.policy};// reset clean policy to be what was just saved
          if (index > -1)  this.policiesAddedClean[index] = {...policy};// reset clean policy to be what was just saved
          let msg = index === -1 ? 'Policy change/s have been saved' : 'Option change/s have been saved';
          this.$store.dispatch('SET_SNACK', { cmpnt: 'EditPolicy', open: true, msg });
        })
        .catch((error) => {
          const msg = index === -1 ? 'Failed to save policy changes.' : 'Failed to save policy option changes.';
          this.$store.dispatch('SET_SNACK', {
            cmpnt: 'EditPolicy',
            color: 'error',
            open: true,
            msg
          });
          console.log('ERROR: ', error);
        });
    },

    scrollIntoView(id) {
        setTimeout(() => {
          let element = document.getElementById(id);
          element.scrollIntoView();
        }, 500);
    },

    valDate(date) {
      let valid = moment(date, this.rulesDateFormat, true).isValid();
      return valid;
    },

  },
};

</script>
<style>
  .top-offset { scroll-margin-top: 48px }
  .changedfield .v-input__append-outer {
    margin-left: 7px !important;
    margin-right: -6px !important;
  }
  .option-details-row .changedfield .v-input__append-outer {
    margin-left: 3px !important; 
    margin-right: -4px !important;
  }
  .changedfield .v-input__append-outer .v-input__icon {
    min-width: 10px !important;
    width: 10px !important;
  }
  .changedfield .v-input__append-outer .v-icon {
    color: firebrick !important;
    font-size: 18px !important;
  }
  .changedfield .v-input__icon--clear {
    min-width: 14px !important;
    width: 14px !important;
    margin-left: 0 !important;
    margin-right: -3px !important;
  }
  .changedfield .v-input__icon--clear .v-icon {
    color: rgba(0, 0, 0, 0.72) !important;
    font-size: 17px !important;
    margin-top: 2px !important;
  }
  .changedfield .v-input__append-inner .v-input__icon--append {
    min-width: 14px !important;
    width: 14px !important;
  }
  .changedfield .v-select__selections {
    padding-right: 0 !important;
  }
  .small label { font-size: 14px !important }
</style>
